// Path: ./src/app/pages/CreateStorePage/websiteFlyerRelease.ts

/**
 * `websiteFlyerReleaseDayTime` is a recurring weekly moment stored as the
 * literal string "<DAY> HH:mm" (SUN-SAT, 24-hour) — no calendar date and no
 * timezone involved. The form edits it as a day-of-week + time-of-day pair.
 */

/** Day tokens the API expects, indexed to match `Date#getDay()` (0 = Sunday). */
export const RELEASE_DAY_TOKENS = [
  "SUN",
  "MON",
  "TUE",
  "WED",
  "THU",
  "FRI",
  "SAT",
];

const RELEASE_DAY_TIME_PATTERN = /^([A-Z]{3})\s+([01]\d|2[0-3]):([0-5]\d)$/;

const parse = (dayTime?: string | null) => {
  const match = RELEASE_DAY_TIME_PATTERN.exec(
    (dayTime ?? "").trim().toUpperCase(),
  );
  if (!match) return null;
  const day = RELEASE_DAY_TOKENS.indexOf(match[1]);
  if (day === -1) return null;
  return { day, time: `${match[2]}:${match[3]}` };
};

/** Day-of-week (0 = Sunday) of a stored release moment. */
export const getReleaseDay = (dayTime?: string | null): number | "" =>
  parse(dayTime)?.day ?? "";

/** Time-of-day ("HH:mm") of a stored release moment. */
export const getReleaseTime = (dayTime?: string | null): string =>
  parse(dayTime)?.time ?? "";

/**
 * Builds the "<DAY> HH:mm" value to send to the API. Returns null when the
 * schedule is incomplete or cleared — the backend treats null as "no schedule".
 */
export const buildReleaseDayTime = (
  day: number | "",
  time: string,
): string | null => {
  if (day === "" || !time) return null;
  const token = RELEASE_DAY_TOKENS[day];
  // <input type="time"> can yield "HH:mm:ss" when a step is set; keep HH:mm.
  const [hours, minutes] = time.split(":");
  if (!token || hours === undefined || minutes === undefined) return null;
  const value = `${token} ${hours.padStart(2, "0")}:${minutes.padStart(2, "0")}`;
  return RELEASE_DAY_TIME_PATTERN.test(value) ? value : null;
};

/** True when only one half of the day/time pair is filled in. */
export const isReleaseScheduleIncomplete = (
  day: number | "",
  time: string,
): boolean => (day === "") !== !time;
